import { supabase } from '../../../shared/lib/supabaseClient';
import { generateUUID } from '../../../shared/lib/uuid';
import { logActivity } from '../../../shared/services/activityLogService';
import { describeFieldChanges, describeRecordDetails } from '../../../shared/utils/describeChanges';

const peso = (value) => `${Number(value) < 0 ? '-' : ''}₱${Math.abs(Number(value)).toLocaleString()}`;

const PAYMENT_FIELD_LABELS = {
  payer_name: 'payer name',
  grade_level: 'grade level',
  school_year: 'school year',
  category: 'payment category',
  amount: 'amount',
  or_number: 'OR number',
  payment_date: 'payment date',
  status: 'status',
  remarks: 'remarks',
};
const formatPaymentValue = (field, value) => (field === 'amount' ? peso(value) : value);

const FETCH_PAGE_SIZE = 1000;

// The API returns at most 1,000 rows per request, so read the table in pages.
export async function getPayments() {
  const allRows = [];

  for (let from = 0; ; from += FETCH_PAGE_SIZE) {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .order('payment_date', { ascending: false })
      .order('id')
      .range(from, from + FETCH_PAGE_SIZE - 1);

    if (error) throw error;
    allRows.push(...data);
    if (data.length < FETCH_PAGE_SIZE) return allRows;
  }
}

export async function createPayment(paymentData) {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) throw userError;

  const { error } = await supabase.from('payments').insert({
    ...paymentData,
    batch_id: generateUUID(),
    created_by: user.id,
  });
  if (error) throw error;
  await logActivity(
    'add_payment',
    `Recorded payment of ${peso(paymentData.amount)} for ${paymentData.payer_name}${paymentData.category ? ` (${paymentData.category})` : ''}`
  );
}

export async function bulkCreatePayments(rows) {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) throw userError;

  const batchId = generateUUID();
  const { error } = await supabase.from('payments').insert(
    rows.map((row) => ({ ...row, batch_id: batchId, created_by: user.id }))
  );
  if (error) throw error;

  const payerName = rows[0]?.payer_name;
  const total = rows.reduce((sum, row) => sum + Number(row.amount), 0);
  const breakdown = rows.map((row) => `${row.category} (${peso(row.amount)})`).join(', ');
  await logActivity(
    'add_payment',
    `Recorded ${rows.length} payments for ${payerName}: ${breakdown} — total ${peso(total)}`
  );
}

export async function updatePayment(id, paymentData, previousData) {
  const { error } = await supabase.from('payments').update(paymentData).eq('id', id);
  if (error) throw error;

  const changes = previousData
    ? describeFieldChanges({
        before: previousData,
        after: paymentData,
        fields: PAYMENT_FIELD_LABELS,
        formatValue: formatPaymentValue,
      })
    : [];
  const description = changes.length
    ? `Updated ${changes.join(', ')} for ${paymentData.payer_name}`
    : `Updated payment for ${paymentData.payer_name}`;
  await logActivity('update_payment', description);
}

export async function deletePayment(id, record) {
  const { error } = await supabase.from('payments').delete().eq('id', id);
  if (error) throw error;

  const displayName = record?.payer_name || id;
  const details = describeRecordDetails({
    record,
    fields: PAYMENT_FIELD_LABELS,
    exclude: ['payer_name'],
    formatValue: formatPaymentValue,
  });
  const description = details.length
    ? `Deleted payment record for ${displayName} (${details.join(', ')})`
    : `Deleted payment record for ${displayName}`;
  await logActivity('delete_payment', description);
}
